import React from "react";
import Parser from "html-react-parser";

const PersonalInfo = () => {
  const datas = [
    {
      id: 1,
      title: "1. Introduction",
      contents: `HERINGS Inc. ("the Company," "we," or "us") values your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and safeguard your information when you use the Healiary Global mobile application, website, and related services ("the Service").<br/><br/>
      We process personal data in accordance with the EU General Data Protection Regulation (GDPR), the UK Data Protection Act 2018, and other applicable international data-protection laws.`
    },
    {
      id: 2,
      title: "2. Data Controller and Contact Information",
      contents: `<strong>Data Controller:</strong> HERINGS Inc.<br/>
      <strong>Registered Address:</strong> 6F, 20, Gukjegeumyung-ro, Yeongdeungpo-gu, Seoul, South Korea<br/>
      <strong>Email:</strong> herings@heringsglobal.com<br/><br/>
      <strong>Data Protection Officer (DPO)</strong><br/>
      <strong>Name:</strong> Jennie Bae<br/>
      <strong>Position:</strong> Assistant Manager, Research & Planning Team<br/>
      <strong>Email:</strong> product@heringsglobal.com`
    },
    {
      id: 3,
      title: "3. Personal Data We Collect",
      contents: `We collect only the data necessary to deliver and improve the Service. The categories include:<br/><br/>
      <strong>a. Account and Identification Data</strong><br/>
      Name, gender, date of birth, email address, mobile number, and login credentials.<br/>
      <strong>Purpose:</strong> Account creation, authentication, support, and security.<br/>
      <strong>Legal Basis:</strong> Contractual necessity (Art. 6(1)(b) GDPR).<br/><br/>

      <strong>b. Health and Lifestyle Data (Required)</strong><br/>
      Cancer type and stage, treatment information, surgery details, chemotherapy records, chronic conditions, medications, allergies, diet and lifestyle habits, physical data (height, weight, activity), symptoms, and mood logs.<br/>
      <strong>Purpose:</strong> To provide personalized healthcare insights and recommendations.<br/>
      <strong>Legal Basis:</strong> Contractual necessity + Explicit consent (Art. 6(1)(b) and Art. 9(2)(a) GDPR).<br/>
      <em>Required Data for Service Use:</em> Because Healiary's core functions depend on health and lifestyle information, this data is required to provide the Service. If you do not agree to its collection and use, you will be unable to create an account or access the Service.<br/><br/>

      <strong>c. Technical and Usage Data</strong><br/>
      Device identifiers (e.g., IDFA/GAID, IMEI), IP address, browser and OS type, cookies, session logs, and app-usage statistics.<br/>
      <strong>Purpose:</strong> Service performance, analytics, and security.<br/>
      <strong>Legal Basis:</strong> Legitimate interests (Art. 6(1)(f) GDPR).<br/><br/>

      <strong>d. Marketing and Communication Data</strong><br/>
      Contact preferences, survey responses, and event participation.<br/>
      <strong>Purpose:</strong> To send updates or promotional information (only with consent).<br/>
      <strong>Legal Basis:</strong> Consent (Art. 6(1)(a) GDPR).`
    },
    {
      id: 4,
      title: "4. How We Collect Personal Data",
      contents: `We obtain data:<br/>
      • Directly from you during registration, questionnaires, or customer support.<br/>
      • Automatically through app or website use (cookies, logs, analytics).<br/>
      • From authorized partners or healthcare institutions where you have provided prior consent.`
    },
    {
      id: 5,
      title: "5. Legal Bases for Processing",
      contents: `We process personal data under the following lawful bases:<br/>
      • <strong>Consent:</strong> for processing special-category data and marketing communications.<br/>
      • <strong>Contractual necessity:</strong> to perform the contract and deliver requested services.<br/>
      • <strong>Legal obligation:</strong> to comply with laws and regulations.<br/>
      • <strong>Legitimate interests:</strong> to maintain, improve, and secure the Service.<br/><br/>
      You may withdraw your consent at any time by contacting privacy@heringsglobal.com. Withdrawal will not affect the lawfulness of prior processing but may prevent continued use of the Service.`
    },
    {
      id: 6,
      title: "6. Data Use, Retention, and Anonymization",
      contents: `We retain personal data only as long as necessary for the purposes stated or to meet legal obligations.<br/><br/>
      <table class="border border-black text-caption2_400 text-gray_120 select-text w-full my-4">
        <thead class="border-b border-black text-caption2_700 text-gray_160">
          <tr>
            <th class="border-r border-black p-2">Data Category</th>
            <th class="p-2">Retention Period</th>
          </tr>
        </thead>
        <tbody>
          <tr class="border-b border-black">
            <td class="border-r border-black p-2">Account data</td>
            <td class="p-2">While your account is active and up to 12 months after deletion</td>
          </tr>
          <tr class="border-b border-black">
            <td class="border-r border-black p-2">Health & lifestyle data</td>
            <td class="p-2">While your account remains active; anonymized upon withdrawal</td>
          </tr>
          <tr class="border-b border-black">
            <td class="border-r border-black p-2">Technical & log data</td>
            <td class="p-2">Up to 12 months after collection</td>
          </tr>
          <tr class="border-b border-black">
            <td class="border-r border-black p-2">Marketing data</td>
            <td class="p-2">Until you unsubscribe or withdraw consent</td>
          </tr>
          <tr class="border-b border-black">
            <td class="border-r border-black p-2">Legal/compliance data</td>
            <td class="p-2">As required by applicable law</td>
          </tr>
        </tbody>
      </table>
      <strong>Data Anonymization Upon Withdrawal</strong><br/>
      When you delete your account or withdraw consent, your personal identifiers are permanently removed and remaining data is irreversibly anonymized for statistical, analytical, or research purposes. Once anonymized, this data is no longer personal data under the GDPR and cannot be re-identified.`
    },
    {
      id: 7,
      title: "7. Data Storage and Hosting",
      contents: `All personal data is stored on Amazon Web Services (AWS) servers located in Frankfurt, Germany (eu-central-1). AWS complies with GDPR and holds security certifications such as ISO 27001, SOC 2, and CSA STAR. Your data is not transferred outside the EEA except where necessary for technical support, and such transfers are protected by lawful safeguards.`
    },
    {
      id: 8,
      title: "8. Data Sharing and Disclosure",
      contents: `We do not sell or rent personal data. We may share it only with:<br/>
      • Service providers bound by strict data-processing agreements;<br/>
      • Legal authorities when required by law;<br/>
      • Business successors in mergers or reorganizations, under equivalent safeguards.`
    },
    {
      id: 9,
      title: "9. Your Data Protection Rights",
      contents: `You have the right to:<br/>
      • Access your data;<br/>
      • Rectify inaccurate data;<br/>
      • Request erasure ("right to be forgotten");<br/>
      • Restrict processing;<br/>
      • Data portability;<br/>
      • Object to processing based on legitimate interests;<br/>
      • Withdraw consent at any time;<br/>
      • Lodge a complaint with your local Data Protection Authority (e.g., CNPD – Luxembourg, BfDI – Germany).<br/><br/>
      Requests can be made via product@heringsglobal.com, and we will respond within one month.`
    },
    {
      id: 10,
      title: "10. Data Security",
      contents: `We implement technical and organizational measures to protect data, including:<br/>
      • Encryption at rest and in transit;<br/>
      • Firewalls and intrusion-detection systems;<br/>
      • Role-based access controls;<br/>
      • Regular vulnerability testing;<br/>
      • Employee confidentiality agreements and training.<br/><br/>
      While we maintain high security standards, no digital service can guarantee absolute protection.`
    },
    {
      id: 11,
      title: "11. Children's Privacy",
      contents: `The Service is not intended for individuals under 16 years of age (or the local minimum age). We do not knowingly collect children's data. If discovered, it will be promptly deleted.`
    },
    {
      id: 12,
      title: "12. Updates to This Policy",
      contents: `We may revise this Policy to reflect changes in law or practice. Revisions will be posted on our website with the updated effective date. For material changes, users will be notified by email or in-app alert.`
    },
    {
      id: 13,
      title: "13. Contact Us",
      contents: `<strong>HERINGS Inc.</strong><br/>
      Email: product@heringsglobal.com<br/>
      Website: <a href='https://heringsglobal.com' class='font-bold'>https://heringsglobal.com</a>`
    },
  ];

  return (
    <div className="text-gray2 keepAll select-text">
      <h1 className="mb-4 text-body3_700 text-gray_160">
        Healiary Global Privacy Policy
      </h1>
      <p className='text-caption2_400 text-gray_120 mb-4'>Version: v1.0<br/>Effective Date: 2025-10-31</p>

      <ul>
        {datas.map((data) => (
          <li key={data.id} className="mb-4">
            <h2 className="text-caption2_700 text-gray_160 mb-2">{data.title}</h2>
            <div className="text-caption2_400 text-gray_120">
              {Parser(data.contents)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PersonalInfo;
